/**
 * GoFundMe Pro's source-attribution params.
 */
const GFMP_PARAMS = ['c_src', 'c_src2'];

/**
 * Standard UTM params.
 */
const UTM_PARAMS = ['utm_campaign', 'utm_medium', 'utm_source'];

const TRACKED_PARAMS = [...GFMP_PARAMS, ...UTM_PARAMS];

/**
 * Pantheon's edge cache replaces the *value* of any `utm_*` (or `__`-prefixed)
 * query param with `PANTHEON_STRIPPED` before the request ever reaches PHP,
 * and can only restore the real value in a redirect's Location header when
 * the redirect doesn't reformulate the URL's structure — which ours does. So
 * when the target is our own `/donate` endpoint (which round-trips through
 * that PHP redirect), the UTM keys must be aliased to something Pantheon
 * doesn't recognize; `class-donation-page-backstop.php`'s `normalize_url()`
 * un-aliases them back before building the final redirect. See DR-1815.
 *
 * @param {string} key The param name.
 * @returns {string} The aliased param name.
 */
function aliasUtmParamName(key) {
  return `dr_${key}`;
}

/**
 * Builds a donation URL, forwarding tracked GoFundMe Pro/UTM params present
 * on the current page. The params are inserted into the query string before
 * the hash fragment (rather than after it) — GoFundMe Pro strips anything
 * appended after the `#`, since that's fragment content and never reaches
 * their server.
 *
 * @param {string} url Configured donation URL.
 * @param {Object} [extraParams] Additional params to set on the URL (e.g. donation amount).
 * @param {Object} [options] Options.
 * @param {boolean} [options.aliasUtmParams] Alias UTM param names so Pantheon's edge cache
 *                                           doesn't recognize and strip their values. Only
 *                                           needed when the URL is our own `/donate` endpoint.
 * @returns {string} The final donation URL.
 */
export function buildDonateUrl(url, extraParams = {}, { aliasUtmParams = false } = {}) {
  const hashIndex = url.indexOf('#');
  const prefix = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);

  const [baseUrl, query] = prefix.split('?');
  const params = new URLSearchParams(query);

  const incomingParams = new URLSearchParams(window.location.search);
  TRACKED_PARAMS.forEach((key) => {
    const value = incomingParams.get(key);
    if (value) {
      const isUtmParam = UTM_PARAMS.includes(key);
      params.set(aliasUtmParams && isUtmParam ? aliasUtmParamName(key) : key, value);
    }
  });

  Object.entries(extraParams).forEach(([key, value]) => {
    params.set(key, value);
  });

  return `${baseUrl}?${params.toString()}${hash}`;
}

export default buildDonateUrl;
